/**
 * AppShell — main 3-panel layout: Sidebar + Chat/Main + Right Panel.
 *
 * Desktop (`md`+) is a true 3-column layout. Below `md` it collapses to a
 * single column suited to phones:
 *   - Sidebar becomes an off-canvas drawer (toggled by the Header hamburger,
 *     dimmed by the backdrop rendered here).
 *   - The right panel's expanded state becomes a full-screen overlay; its
 *     collapsed edge strip is hidden (reached instead via the Header button).
 *
 * The right panel (desktop) has two states:
 *   - Expanded: full tabbed panel (440px) with shortlist/artifacts content
 *   - Collapsed: narrow vertical tab strip along the right edge
 *
 * Routing is handled by react-router; this component renders the shared
 * chrome (Header, Sidebar, RightPanel) around an <Outlet />.
 */

import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { RightPanel, CollapsedRightPanel } from "@/components/artifacts/RightPanel";
import { SearchModal } from "@/components/search/SearchModal";
import { SubagentModal } from "@/components/chat/SubagentModal";
import { useNavStore } from "@/stores/navStore";
import { useChatStore } from "@/stores/chatStore";
import { useSearchStore } from "@/stores/searchStore";

export function AppShell() {
  const rightPanelOpen = useChatStore((s) => s.rightPanelOpen);
  const mobileNavOpen = useNavStore((s) => s.mobileNavOpen);
  const closeMobileNav = useNavStore((s) => s.closeMobileNav);
  const navigate = useNavigate();
  const { conversationId } = useParams();

  // Register the router's navigate so Zustand stores can use it imperatively
  const register = useNavStore((s) => s.register);
  useEffect(() => {
    register(navigate);
  }, [navigate, register]);

  // Global Cmd/Ctrl+K toggles the search palette from anywhere in the app.
  // Bound once on the shell (always mounted) — there is no other global
  // keydown handler to collide with.
  const toggleSearch = useSearchStore((s) => s.toggleSearch);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        toggleSearch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleSearch]);

  // Sync URL → store: when the URL has a conversationId, open it in the store
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const openConversation = useChatStore((s) => s.openConversation);
  useEffect(() => {
    if (conversationId && conversationId !== activeConversationId) {
      openConversation(conversationId);
    }
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-dvh-screen flex flex-col overflow-hidden">
      <Header />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar />

        {/* Mobile drawer backdrop — dims the content while the off-canvas
            sidebar is open; a tap anywhere on it closes the drawer. Sits
            under the drawer (z-40) but over the content, and never renders on
            desktop (the rail is always in-flow there). */}
        {mobileNavOpen && (
          <div
            className="md:hidden fixed inset-0 z-30 bg-black/40 animate-[fadeIn_150ms_ease-out]"
            onClick={closeMobileNav}
            aria-hidden="true"
          />
        )}

        {/* Main content area */}
        <main className="flex-1 flex overflow-hidden min-w-0">
          <div className="flex-1 overflow-hidden min-w-0">
            <Outlet />
          </div>

          {/* Right panel: expanded or collapsed tab strip. Expanded is an
              inline 440px column on desktop, but a full-screen overlay on
              mobile (fixed inset-0) so it doesn't compete for width with the
              chat. The collapsed strip is desktop-only (see CollapsedRightPanel). */}
          {conversationId && (
            rightPanelOpen ? (
              <div className="fixed inset-0 z-40 bg-surface pt-[env(safe-area-inset-top)] panel-transition
                              md:static md:inset-auto md:z-auto md:pt-0
                              md:w-[440px] md:border-l md:border-space-30 md:shrink-0">
                <RightPanel />
              </div>
            ) : (
              <CollapsedRightPanel />
            )
          )}
        </main>
      </div>

      {/* Global search palette (Cmd/Ctrl+K or sidebar launcher) */}
      <SearchModal />

      {/* Sub-agent drill-in — opened from the "Working" indicator or a Task
          tool card. Mounted here so it overlays the whole shell. */}
      <SubagentModal />
    </div>
  );
}
